import React from 'react';
import { useTranslation } from 'react-i18next';
import {
    DialogContent,
    DefaultButton,
    DialogActions,
    DialogContentText,
    TextField,
    PrimaryButton,
} from '@bb-ui/react-library';
import CircularProgress from '@material-ui/core/CircularProgress/CircularProgress';
import { User } from '../../../../../contexts/LicenseManagementContext/LicenseManagementContext.types';
import { mapToMenuItems } from '../../../../../utilities/selectInput';
import { AUTHOR_ROLE_NAME } from '../../roles';
import { useLicenseManagementContext } from '../../../../../contexts/LicenseManagementContext';

const PICK_SOMETHING_PH = 'PICK_SOMETHING';

export const TransferAssetsDialogContent = (props: {
    userToTransferFrom: User | undefined;
    transferInProgress: boolean;
    closeDialog: () => void;
    transferAssets: (props: {
        fromUser: User;
        target: { userId: string } | { group: string };
    }) => void;
    infoText: string;
}) => {
    const { t } = useTranslation();
    const {
        userToTransferFrom,
        transferAssets,
        transferInProgress,
        closeDialog,
        infoText,
    } = props;
    const { users } = useLicenseManagementContext();

    const [targetInputState, setTargetInputState] =
        React.useState<string>(PICK_SOMETHING_PH);

    const authorMenuItems = React.useMemo(
        () =>
            users
                ?.filter(
                    user =>
                        user.userId !== userToTransferFrom?.userId &&
                        user.roles?.map(r => r.name).includes(AUTHOR_ROLE_NAME),
                )
                .map(user => ({
                    key: `user:${user.userId}`,
                    name: user.userName,
                })) || [],
        [users, userToTransferFrom],
    );

    const onTransferButtonClick = React.useMemo(
        () => () => {
            if (!userToTransferFrom) {
                return;
            }
            const groups = targetInputState.match(/^([^:]*):(.*)$/);

            const [targetType, targetValue] = [groups?.at(1), groups?.at(2)];

            if (!targetType || !targetValue) {
                return;
            }

            transferAssets({
                fromUser: userToTransferFrom,
                target:
                    targetType === 'user'
                        ? { userId: targetValue! }
                        : { group: targetValue! },
            });
        },
        [targetInputState, transferAssets, userToTransferFrom],
    );

    return (
        <>
            <DialogContent
                style={{
                    minHeight: '160px',
                    // top and bottom "margin" the same
                    justifyContent: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                }}
            >
                <DialogContentText>{infoText}</DialogContentText>
                <TextField
                    select
                    id="transfer-assets-target-picker"
                    label={t(
                        'settings.licenseManagement.assetTransfer.selectTargetLabel',
                    )}
                    inputProps={{ 'data-testid': 'transfer-assets-target' }}
                    value={targetInputState}
                    onChange={(event: any) => {
                        setTargetInputState(event.target.value);
                    }}
                    fullWidth
                    data-testid="asset-transfer-target-picker"
                >
                    {mapToMenuItems([
                        {
                            key: PICK_SOMETHING_PH,
                            name: t(
                                'settings.licenseManagement.assetTransfer.inputRequired',
                            ),
                            invisible: true,
                        },
                        {
                            key: `group:${AUTHOR_ROLE_NAME}`,
                            name: t(
                                'settings.licenseManagement.assetTransfer.toAllAuthors',
                            ),
                        },
                        ...authorMenuItems,
                    ])}
                </TextField>
            </DialogContent>
            <DialogActions>
                <DefaultButton
                    disabled={transferInProgress}
                    data-testid="asset-transfer-cancel"
                    onClick={closeDialog}
                >
                    {t('settings.licenseManagement.assetTransfer.cancel')}
                </DefaultButton>
                <PrimaryButton
                    disabled={
                        PICK_SOMETHING_PH === targetInputState ||
                        transferInProgress
                    }
                    onClick={onTransferButtonClick}
                    data-testid="asset-transfer-confirm"
                >
                    {transferInProgress ? (
                        <CircularProgress
                            aria-label={t(
                                'settings.licenseManagement.assetTransfer.inProgress',
                            )}
                            size={18}
                            data-testid="asset-transfer-in-progress"
                        />
                    ) : (
                        t('settings.licenseManagement.assetTransfer.ok')
                    )}
                </PrimaryButton>
            </DialogActions>
        </>
    );
};
